(() => {
  const manualPlaybackVideos = new WeakSet();

  const isVideoVisibleInViewport = (video) => {
    const rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  };

  const getViewportIntersectionRatio = (element) => {
    const rect = element.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!area) return 0;

    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return (visibleWidth * visibleHeight) / area;
  };

  const enableManualVideoPlayback = (video) => {
    video.controls = true;
    video.closest('.cts-ugc__media, .cts-review-placeholder__media, .cts-responsive-media')?.classList.add('cts-video-needs-tap');
  };

  const createVideoPlayButton = (video, onPlay) => {
    const container = video.parentElement;
    if (!container || container.querySelector(':scope > .cts-video-play-button')) return;

    container.classList.add('cts-video-controls-container');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cts-video-play-button';
    button.setAttribute('aria-label', `Play ${video.getAttribute('aria-label') || 'video'}`);
    button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.8v12.4c0 .8.9 1.3 1.6.9l9.5-6.2a1 1 0 0 0 0-1.7L9.6 5a1 1 0 0 0-1.6.8Z" fill="currentColor"/></svg>';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      manualPlaybackVideos.add(video);
      onPlay();
    });
    video.addEventListener('playing', () => { button.hidden = true; });
    video.addEventListener('pause', () => { button.hidden = false; });
    video.addEventListener('ended', () => { button.hidden = false; });
    container.append(button);
  };

  const playVideoFromTap = (video, withSound = false) => {
    if (!video) return;
    manualPlaybackVideos.add(video);
    video.removeAttribute('loading');
    video.autoplay = false;
    video.playsInline = true;
    video.muted = !withSound;
    video.defaultMuted = !withSound;
    video.volume = withSound ? 1 : 0;

    try {
      video.preload = 'auto';
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
      const playback = video.play();
      if (playback) playback.catch(() => enableManualVideoPlayback(video));
    } catch {
      enableManualVideoPlayback(video);
    }
  };

  const syncLooxProduct = () => {
    const authority = document.querySelector('cts-product-purchase[data-product-id]');
    const productId = authority?.dataset.productId;

    document.querySelectorAll('loox-dynamic-carousel-widget.cts-reviews__dynamic-carousel').forEach((carousel) => {
      if (!productId) return;
      carousel.setAttribute('product-id', productId);
    });

    document.querySelectorAll('cts-loox-reviews').forEach((host) => {
      if (!productId) return;

      let container = host.querySelector('[data-cts-loox-container]');

      if (!container) {
        const mount = host.querySelector('[data-cts-loox-mount]');
        if (!mount) return;

        container = document.createElement('div');
        container.id = 'looxReviews';
        container.className = 'loox-widget';
        container.dataset.ctsLooxContainer = '';
        container.dataset.limit = host.dataset.reviewLimit || '6';
        container.dataset.productThumbnails = 'false';
        container.dataset.reviewsToDisplay = '';
        container.dataset.defaultTab = 'automatic';
        mount.append(container);
      }

      container.dataset.productId = productId;
      host.dataset.productContext = 'ready';
    });
  };

  const reviewDialogControllers = new WeakMap();
  const looxReviewSeeMoreButtons = new WeakSet();
  const looxReviewCardControllers = new WeakMap();
  const storyVideoControllers = new WeakMap();

  const syncSeeMoreVisibility = (reviewText, button) => {
    if (!reviewText || !button) return;
    const isClipped = reviewText.scrollHeight > reviewText.clientHeight + 1;
    if (button.hidden === isClipped) button.hidden = !isClipped;
  };

  const getReviewDialog = (section) => {
    const modalId = section?.dataset.ctsReviewModalId;
    return modalId ? document.getElementById(modalId) : null;
  };

  const showReviewDialog = (modal, opener, { text, reviewer, rating, media } = {}) => {
    if (!modal || typeof modal.show !== 'function' || !text?.trim()) return false;

    const quote = modal.querySelector('[data-cts-review-modal-quote]');
    const attribution = modal.querySelector('[data-cts-review-modal-attribution]');
    const ratingContainer = modal.querySelector('[data-cts-review-modal-rating]');
    const mediaContainer = modal.querySelector('[data-cts-review-modal-media]');
    if (!quote || !attribution || !ratingContainer || !mediaContainer) return false;

    quote.textContent = text.trim();
    attribution.textContent = reviewer?.trim() || '';
    attribution.hidden = !attribution.textContent;

    ratingContainer.replaceChildren();
    const sourceRating = rating instanceof Element ? rating : rating?.querySelector?.('*');
    const ratingAsset = sourceRating?.cloneNode(true);
    if (ratingAsset instanceof Element) {
      ratingAsset.removeAttribute('id');
      ratingAsset.classList.add('cts-review-modal__rating-value');
      ratingContainer.append(ratingAsset);
    }
    ratingContainer.hidden = !ratingAsset;

    mediaContainer.replaceChildren();
    const sourceMedia = media instanceof Element && media.matches('img, video')
      ? media
      : media?.querySelector?.('img, video');
    const mediaAsset = sourceMedia?.cloneNode(true);

    if (mediaAsset) {
      mediaAsset.className = 'cts-review-modal__asset';
      mediaAsset.removeAttribute('id');

      if (mediaAsset instanceof HTMLVideoElement) {
        mediaAsset.pause();
        mediaAsset.removeAttribute('autoplay');
        mediaAsset.autoplay = false;
        mediaAsset.controls = false;
        mediaAsset.loop = false;
        mediaAsset.muted = false;
        mediaAsset.defaultMuted = false;
        mediaAsset.volume = 1;
        mediaAsset.playsInline = true;
        mediaAsset.preload = 'none';
        mediaAsset.removeAttribute('loading');
        if (!mediaAsset.getAttribute('aria-label')) mediaAsset.setAttribute('aria-label', 'Customer review video');
        if (mediaAsset.poster) {
          const poster = document.createElement('img');
          poster.className = 'cts-review-modal__poster';
          poster.src = mediaAsset.poster;
          poster.alt = '';
          poster.setAttribute('aria-hidden', 'true');
          poster.loading = 'eager';
          poster.decoding = 'async';
          mediaContainer.append(poster);
        }
        const playButton = document.createElement('button');
        playButton.type = 'button';
        playButton.className = 'cts-review-modal__play';
        playButton.setAttribute('aria-label', 'Play customer review video');
        playButton.textContent = '▶';
        playButton.addEventListener('click', () => {
          mediaAsset.controls = true;
          playButton.hidden = true;
          const playAttempt = mediaAsset.play();
          playAttempt?.catch(() => {
            playButton.hidden = false;
          });
        });
        mediaContainer.append(playButton);
      } else if (mediaAsset instanceof HTMLImageElement) {
        mediaAsset.loading = 'eager';
        mediaAsset.decoding = 'async';
      }

      mediaContainer.append(mediaAsset);
    }

    const sourceVideo = sourceMedia instanceof HTMLVideoElement
      ? sourceMedia
      : sourceMedia?.querySelector?.('video');
    sourceVideo?.pause();

    mediaContainer.hidden = !mediaAsset;
    modal.classList.toggle('cts-review-modal--text-only', !mediaAsset);
    modal.show(opener);
    return true;
  };

  const initReviewDialogButtons = (scope = document) => {
    const sections = [];
    if (scope instanceof Element && scope.matches('.cts-reviews')) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll('.cts-reviews'));

    sections.forEach((section) => {
      if (reviewDialogControllers.has(section)) return;

      const handleClick = (event) => {
        const opener = event.target instanceof Element
          ? event.target.closest('.cts-review-placeholder__see-more')
          : null;
        if (!opener || !section.contains(opener)) return;

        const card = opener.closest('.cts-review-placeholder');
        const quote = card?.querySelector('.cts-review-placeholder__quote');
        if (!card || !quote) return;

        if (showReviewDialog(
          getReviewDialog(section),
          opener,
          {
            text: quote.textContent || '',
            reviewer: card.querySelector('.cts-review-placeholder__footer p')?.textContent || '',
            rating: card.querySelector('.cts-review-placeholder__stars'),
            media: card.querySelector('.cts-review-placeholder__media'),
          }
        )) event.preventDefault();
      };

      section.addEventListener('click', handleClick);
      const syncStaticReviewButtons = () => {
        section.querySelectorAll('.cts-review-placeholder').forEach((card) => {
          syncSeeMoreVisibility(
            card.querySelector('.cts-review-placeholder__quote'),
            card.querySelector('.cts-review-placeholder__see-more')
          );
        });
      };
      const overflowObserver = 'ResizeObserver' in window
        ? new ResizeObserver(syncStaticReviewButtons)
        : null;
      section.querySelectorAll('.cts-review-placeholder__quote').forEach((quote) => overflowObserver?.observe(quote));
      window.addEventListener('resize', syncStaticReviewButtons, { passive: true });
      syncStaticReviewButtons();
      window.requestAnimationFrame(syncStaticReviewButtons);
      document.fonts?.ready?.then(syncStaticReviewButtons);

      reviewDialogControllers.set(section, {
        destroy() {
          section.removeEventListener('click', handleClick);
          overflowObserver?.disconnect();
          window.removeEventListener('resize', syncStaticReviewButtons);
          reviewDialogControllers.delete(section);
        },
      });
    });
  };

  const looxPlaceholderCardStyles = `
    .dynamic-carousel-component-container {
      padding-bottom: 0 !important;
    }

    .dynamic-carousel-sliders-grid,
    .dynamic-carousel-sliders-grid.show-review-text {
      display: block !important;
    }

    .dynamic-carousel-image-swiper-container {
      display: none !important;
    }

    .dynamic-carousel-text-swiper-slide {
      height: auto !important;
    }

    .dynamic-carousel-text-swiper-content {
      display: flex !important;
      flex-direction: column !important;
      gap: 1.2rem !important;
      height: auto !important;
      min-height: 0 !important;
      padding: 2.2rem !important;
      border: 0.1rem solid rgba(17, 17, 17, 0.18) !important;
      border-radius: 0 !important;
      background: #ffffff !important;
      color: #111111 !important;
      text-align: left !important;
      overflow: hidden !important;
    }

    .dynamic-carousel-text-swiper-content .review-text {
      --loox-u-dynamic-carousel-review-max-lines: 3 !important;
      --loox-u-dynamic-carousel-review-font-size: calc(var(--font-heading-scale) * 1.2rem) !important;
      --loox-u-dynamic-carousel-review-line-height: calc(1 + 0.3 / max(1, var(--font-heading-scale))) !important;
      justify-self: stretch !important;
      max-height: calc(var(--font-heading-scale) * 1.2rem * 1.3 * 3) !important;
      overflow: hidden !important;
      color: rgba(17, 17, 17, 0.72) !important;
      font-family: var(--font-heading-family) !important;
      font-style: var(--font-heading-style) !important;
      font-weight: var(--font-heading-weight) !important;
      font-size: calc(var(--font-heading-scale) * 1.2rem) !important;
      line-height: calc(1 + 0.3 / max(1, var(--font-heading-scale))) !important;
      text-align: left !important;
      -webkit-box-orient: vertical !important;
      -webkit-line-clamp: 3 !important;
      display: -webkit-box !important;
    }

    .cts-review-see-more {
      display: inline-flex;
      min-height: 4.4rem;
      align-items: center;
      align-self: flex-start;
      margin: 0;
      padding: 0;
      border: 0;
      background: transparent;
      color: #9f4720;
      font: inherit;
      font-weight: 700;
      line-height: 1.4;
      text-align: left;
      text-decoration: underline;
      text-decoration-thickness: 0.1rem;
      text-underline-offset: 0.3rem;
      cursor: pointer;
    }

    .cts-review-see-more[hidden] {
      display: none !important;
    }

    .cts-review-see-more:hover {
      color: #713116;
      text-decoration-thickness: 0.2rem;
    }

    .cts-review-see-more:focus-visible {
      outline: 0.2rem solid #9f4720;
      outline-offset: 0.4rem;
    }

    .dynamic-carousel-text-swiper-content .dynamic-carousel-review-rating {
      justify-self: start !important;
    }

    .cts-loox-review-media {
      display: block;
      width: 100%;
      aspect-ratio: 3 / 5;
      object-fit: cover;
      background: rgba(17, 17, 17, 0.06);
      border: 0.1rem solid rgba(17, 17, 17, 0.12);
    }

    .cts-video-controls-container {
      position: relative !important;
    }

    .cts-video-play-button {
      position: absolute;
      z-index: 5;
      top: 50%;
      left: 50%;
      display: grid;
      width: 5.6rem;
      height: 5.6rem;
      place-items: center;
      padding: 0;
      transform: translate(-50%, -50%);
      border: 0.1rem solid rgba(255, 255, 255, 0.8);
      border-radius: 50%;
      background: rgba(17, 17, 17, 0.78);
      color: #ffffff;
      cursor: pointer;
    }

    .cts-video-play-button[hidden] {
      display: none !important;
    }

    .cts-video-play-button svg {
      width: 2.4rem;
      height: 2.4rem;
    }

    .dynamic-carousel-image-icon,
    .dynamic-carousel-video-icon,
    .dynamic-carousel-play-icon,
    [class*='play-icon'],
    [class*='media-icon'] {
      display: none !important;
    }

    .cts-loox-review-title {
      margin: 0;
      color: #111111;
      font-size: 1.8rem;
      font-weight: 600;
      line-height: 1.2;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
    }

    .dynamic-carousel-text-swiper-content .reviewer-name-container {
      justify-self: start !important;
      align-self: stretch !important;
      width: 100%;
      margin-top: auto !important;
      padding-top: 1.8rem;
      border-top: 0.1rem solid rgba(17, 17, 17, 0.14);
    }

    @media screen and (max-width: 1024px), (hover: none) and (pointer: coarse) {
      .dynamic-carousel-text-swiper-content .review-text {
        max-height: calc(var(--font-heading-scale) * 1.2rem * 1.3 * 3) !important;
      }
    }

    @media screen and (min-width: 1025px) and (hover: hover) and (pointer: fine) {
      .dynamic-carousel-text-swiper-content .review-text {
        --loox-u-dynamic-carousel-review-font-size: calc(var(--font-heading-scale) * 1.3rem) !important;
        font-size: calc(var(--font-heading-scale) * 1.3rem) !important;
        max-height: calc(var(--font-heading-scale) * 1.3rem * 1.3 * 3) !important;
      }
    }
  `;

  const getLooxReviewWidgets = (scope = document) => {
    const widgets = [];

    if (scope instanceof Element && scope.matches('loox-dynamic-carousel-widget[data-cts-placeholder-card-layout]')) {
      widgets.push(scope);
    }
    if (scope.querySelectorAll) {
      widgets.push(...scope.querySelectorAll('loox-dynamic-carousel-widget[data-cts-placeholder-card-layout]'));
    }

    return widgets;
  };

  const shortReviewTitle = (reviewText) => {
    const text = reviewText.replace(/\s+/g, ' ').trim();
    if (!text) return 'Customer review';

    const sentence = text.split(/(?<=[.!?])\s/)[0] || text;
    if (sentence.length <= 52) return sentence;

    const words = sentence.split(' ');
    let title = '';

    for (const word of words) {
      const nextTitle = `${title}${title ? ' ' : ''}${word}`;
      if (nextTitle.length > 48) break;
      title = nextTitle;
    }

    return `${title || sentence.slice(0, 48).trim()}…`;
  };

  const cloneLooxReviewMedia = (mediaSlide) => {
    const source = mediaSlide?.querySelector('img, video');
    if (!source) return null;

    const media = source.cloneNode(true);
    media.className = 'cts-loox-review-media';
    media.removeAttribute('id');

    if (media instanceof HTMLVideoElement) {
      media.controls = false;
      media.muted = true;
      media.defaultMuted = true;
      media.autoplay = true;
      media.playsInline = true;
      media.preload = 'auto';
    }

    return media;
  };

  const decorateLooxReviewCards = (root, resizeObserver, observedReviewTexts) => {
    const mediaSlides = [...root.querySelectorAll('.dynamic-carousel-image-swiper-slide')];
    const textSlides = [...root.querySelectorAll('.dynamic-carousel-text-swiper-slide')];

    textSlides.forEach((slide, index) => {
      const content = slide.querySelector('.dynamic-carousel-text-swiper-content');
      const reviewText = content?.querySelector('.review-text');
      if (!content || !reviewText) return;

      const slideIndex = slide.dataset.swiperSlideIndex;
      const matchingMediaSlide = mediaSlides.find((mediaSlide) => mediaSlide.dataset.swiperSlideIndex === slideIndex) || mediaSlides[index];
      const rating = content.querySelector('.dynamic-carousel-review-rating');
      let title = content.querySelector('.cts-loox-review-title');

      if (!title) {
        title = document.createElement('h3');
        title.className = 'cts-loox-review-title';
        title.textContent = shortReviewTitle(reviewText.textContent || '');
      }

      let media = content.querySelector('.cts-loox-review-media');
      if (!media) media = cloneLooxReviewMedia(matchingMediaSlide);
      if (!media) {
        media = document.createElement('div');
        media.className = 'cts-loox-review-media';
        media.setAttribute('aria-hidden', 'true');
      }

      if (media) {
        if (rating && rating.nextElementSibling !== media) {
          rating.insertAdjacentElement('afterend', media);
        } else if (!rating && content.firstElementChild !== media) {
          content.prepend(media);
        }
      }

      if (media && media.nextElementSibling !== title) {
        media.insertAdjacentElement('afterend', title);
      } else if (!media && reviewText.previousElementSibling !== title) {
        reviewText.insertAdjacentElement('beforebegin', title);
      }

      const fullText = reviewText.textContent?.trim();
      if (!fullText) {
        content.querySelector('.cts-review-see-more')?.remove();
        return;
      }

      let seeMore = content.querySelector('.cts-review-see-more');
      if (!seeMore) {
        seeMore = document.createElement('button');
        seeMore.type = 'button';
        seeMore.className = 'cts-review-see-more';
        seeMore.textContent = 'See more';
        seeMore.hidden = true;
        seeMore.setAttribute('aria-haspopup', 'dialog');
      }

      const section = widget.closest('.cts-reviews');
      const modalId = widget.dataset.ctsReviewModalId || section?.dataset.ctsReviewModalId;
      if (modalId) {
        seeMore.setAttribute('aria-controls', modalId);
        const reviewerName = content.querySelector('.reviewer-name-container')?.textContent?.trim();
        seeMore.setAttribute('aria-label', `See more of review by ${reviewerName || 'customer'}`);
      }
      if (reviewText.nextElementSibling !== seeMore) reviewText.insertAdjacentElement('afterend', seeMore);

      if (!looxReviewSeeMoreButtons.has(seeMore)) {
        seeMore.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const activeContent = seeMore.closest('.dynamic-carousel-text-swiper-content');
          const activeReviewText = activeContent?.querySelector('.review-text');
          if (!activeReviewText) return;

          showReviewDialog(
            getReviewDialog(section),
            seeMore,
            {
              text: activeReviewText.textContent || '',
              reviewer: activeContent.querySelector('.reviewer-name-container')?.textContent || '',
              rating: activeContent.querySelector('.dynamic-carousel-review-rating'),
              media: activeContent.querySelector('.cts-loox-review-media'),
            }
          );
        });
        looxReviewSeeMoreButtons.add(seeMore);
      }

      syncSeeMoreVisibility(reviewText, seeMore);
      if (!observedReviewTexts.has(reviewText)) {
        resizeObserver?.observe(reviewText);
        observedReviewTexts.add(reviewText);
      }
    });
  };

  const initLooxPlaceholderCards = (scope = document) => {
    getLooxReviewWidgets(scope).forEach((widget) => {
      if (looxReviewCardControllers.has(widget)) return;

      let attempts = 0;
      let rootObserver;
      const observedReviewTexts = new WeakSet();
      const reviewTextResizeObserver = 'ResizeObserver' in window
        ? new ResizeObserver((entries) => {
          entries.forEach(({ target }) => {
            const button = target.closest('.dynamic-carousel-text-swiper-content')?.querySelector('.cts-review-see-more');
            syncSeeMoreVisibility(target, button);
          });
        })
        : null;
      const syncLooxReviewButtons = () => {
        const root = widget.shadowRoot;
        root?.querySelectorAll('.dynamic-carousel-text-swiper-content .review-text').forEach((reviewText) => {
          const button = reviewText.closest('.dynamic-carousel-text-swiper-content')?.querySelector('.cts-review-see-more');
          syncSeeMoreVisibility(reviewText, button);
        });
      };
      const reviewVideoObservers = new Map();

      const pauseReviewVideos = () => {
        reviewVideoObservers.forEach((_, video) => {
          manualPlaybackVideos.delete(video);
          if (!video.paused) video.pause();
        });
      };

      const initReviewVideoPlayback = (root) => {
        root.querySelectorAll('video.cts-loox-review-media').forEach((video) => {
          if (reviewVideoObservers.has(video)) return;

          video.controls = false;
          video.muted = true;
          video.defaultMuted = true;
          video.autoplay = true;
          video.playsInline = true;
          video.preload = 'none';
          createVideoPlayButton(video, () => playVideoFromTap(video));

          const updatePlayback = (intersectionRatio) => {
            if (document.hidden || intersectionRatio < 0.35) {
              if (manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video) && !document.hidden) return;
              manualPlaybackVideos.delete(video);
              if (!video.paused) video.pause();
              return;
            }

            const playback = video.play();
            if (playback) playback.catch(() => {
              enableManualVideoPlayback(video);
              video.pause();
            });
          };

          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(([entry]) => {
              updatePlayback(entry.isIntersecting ? entry.intersectionRatio : 0);
            }, { threshold: [0, 0.35] });
            observer.observe(video);
            reviewVideoObservers.set(video, { observer, video });
          } else {
            const onScroll = () => updatePlayback(getViewportIntersectionRatio(video));
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll);
            onScroll();
            reviewVideoObservers.set(video, { onScroll, video });
          }
        });
      };

      const handleReviewVisibilityChange = () => {
        if (document.hidden) pauseReviewVideos();
      };

      const connect = () => {
        const root = widget.shadowRoot;

        if (!root) {
          if (attempts < 30) {
            attempts += 1;
            window.setTimeout(connect, 200);
          }
          return;
        }

        if (!root.querySelector('[data-cts-loox-review-card-styles]')) {
          const style = document.createElement('style');
          style.dataset.ctsLooxReviewCardStyles = '';
          style.textContent = looxPlaceholderCardStyles;
          root.append(style);
        }

        decorateLooxReviewCards(root, reviewTextResizeObserver, observedReviewTexts);
        initReviewVideoPlayback(root);
        rootObserver = new MutationObserver(() => {
          decorateLooxReviewCards(root, reviewTextResizeObserver, observedReviewTexts);
          initReviewVideoPlayback(root);
        });
        rootObserver.observe(root, { childList: true, subtree: true, characterData: true });
        window.addEventListener('resize', syncLooxReviewButtons, { passive: true });
        document.addEventListener('visibilitychange', handleReviewVisibilityChange);
      };

      connect();

      looxReviewCardControllers.set(widget, {
        destroy() {
          rootObserver?.disconnect();
          reviewTextResizeObserver?.disconnect();
          window.removeEventListener('resize', syncLooxReviewButtons);
          pauseReviewVideos();
          reviewVideoObservers.forEach(({ observer, onScroll }) => {
            observer?.disconnect();
            if (onScroll) {
              window.removeEventListener('scroll', onScroll);
              window.removeEventListener('resize', onScroll);
            }
          });
          reviewVideoObservers.clear();
          document.removeEventListener('visibilitychange', handleReviewVisibilityChange);
          looxReviewCardControllers.delete(widget);
        },
      });
    });
  };

  const customerMediaControllers = new WeakMap();
  const swipeRailControllers = new WeakMap();
  let mobileGradientParallaxController;

  const initMobileGradientParallax = () => {
    if (mobileGradientParallaxController) return;

    const mobileQuery = window.matchMedia('(max-width: 1024px)');
    const touchMobileQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if ((!mobileQuery.matches && !touchMobileQuery.matches) || reduceMotionQuery.matches) return;

    const bands = [...document.querySelectorAll(
      '.cts-landing-page .cts-mechanism.gradient, .cts-landing-page .cts-ugc.gradient, .cts-landing-page .cts-shipping-returns.gradient, .cts-landing-page .cts-closing-offer.gradient, .cts-landing-page .cts-testimonial-banner.gradient'
    )];
    if (!bands.length) return;

    let frame;
    const update = () => {
      frame = undefined;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      bands.forEach((band) => {
        const bounds = band.getBoundingClientRect();
        const travel = viewportHeight + bounds.height;
        const progress = Math.min(1, Math.max(0, (viewportHeight - bounds.top) / travel));
        band.style.setProperty('--cts-mobile-gradient-position', `${20 + progress * 60}%`);
      });
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    requestUpdate();

    mobileGradientParallaxController = {
      destroy() {
        if (frame) window.cancelAnimationFrame(frame);
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
        bands.forEach((band) => band.style.removeProperty('--cts-mobile-gradient-position'));
        mobileGradientParallaxController = undefined;
      },
    };
  };

  const getSwipeRails = (scope = document) => {
    const rails = [];

    if (scope instanceof Element && scope.matches('[data-cts-swipe-rail]')) rails.push(scope);
    if (scope.querySelectorAll) rails.push(...scope.querySelectorAll('[data-cts-swipe-rail]'));

    return rails;
  };

  const initSwipeRails = (scope = document) => {
    getSwipeRails(scope).forEach((rail) => {
      if (swipeRailControllers.has(rail)) return;

      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startScrollLeft = 0;
      let isDragging = false;
      let ignoreClick = false;

      const endDrag = (event) => {
        if (pointerId !== event.pointerId) return;

        if (isDragging && rail.hasPointerCapture?.(pointerId)) rail.releasePointerCapture(pointerId);
        ignoreClick = isDragging;
        rail.classList.remove('is-dragging');
        pointerId = null;
        isDragging = false;
      };

      const handlePointerDown = (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startScrollLeft = rail.scrollLeft;
        isDragging = false;
      };

      const handlePointerMove = (event) => {
        if (pointerId !== event.pointerId) return;

        const horizontalDistance = startX - event.clientX;
        const verticalDistance = startY - event.clientY;

        if (!isDragging) {
          if (Math.abs(horizontalDistance) < 6) return;
          if (Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) {
            pointerId = null;
            return;
          }

          isDragging = true;
          rail.setPointerCapture?.(pointerId);
          rail.classList.add('is-dragging');
        }

        rail.scrollLeft = startScrollLeft + horizontalDistance;
        event.preventDefault();
      };

      const handleClick = (event) => {
        if (!ignoreClick) return;
        event.preventDefault();
        event.stopPropagation();
        ignoreClick = false;
      };

      const handleKeydown = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        rail.scrollBy({
          left: (event.key === 'ArrowRight' ? 1 : -1) * Math.max(rail.clientWidth * 0.9, 240),
          behavior: 'smooth',
        });
      };

      rail.addEventListener('pointerdown', handlePointerDown);
      rail.addEventListener('pointermove', handlePointerMove, { passive: false });
      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);
      rail.addEventListener('click', handleClick, true);
      rail.addEventListener('keydown', handleKeydown);

      swipeRailControllers.set(rail, {
        destroy() {
          rail.removeEventListener('pointerdown', handlePointerDown);
          rail.removeEventListener('pointermove', handlePointerMove);
          rail.removeEventListener('pointerup', endDrag);
          rail.removeEventListener('pointercancel', endDrag);
          rail.removeEventListener('click', handleClick, true);
          rail.removeEventListener('keydown', handleKeydown);
          swipeRailControllers.delete(rail);
        },
      });
    });
  };

  const getCustomerMediaSections = (scope = document) => {
    const sections = [];

    if (scope instanceof Element && scope.matches('.cts-ugc')) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll('.cts-ugc'));

    return sections;
  };

  const initCustomerMediaPlayback = (scope = document) => {
    const supportsIntersectionObserver = 'IntersectionObserver' in window;

    getCustomerMediaSections(scope).forEach((section) => {
      if (customerMediaControllers.has(section)) return;

      const videos = [...section.querySelectorAll('.cts-ugc__video')];
      if (!videos.length) return;

      const rail = section.querySelector('[data-cts-swipe-rail]');
      const swipeEndEvent = window.PointerEvent ? 'pointerup' : 'touchend';
      const mobileOrTabletQuery = window.matchMedia('(max-width: 1366px), (any-pointer: coarse)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const isMobileOrTablet = mobileOrTabletQuery.matches;
      const autoPlayOnReveal = isMobileOrTablet && !reducedMotionQuery.matches;
      const soundButtons = [...section.querySelectorAll('[data-cts-ugc-sound-toggle]')];
      let soundEnabled = false;
      let activeVideo = null;
      let hasSwipeInteraction = false;
      let sectionIsVisible = videos.some(isVideoVisibleInViewport);
      let playbackAttempt = 0;

      const syncSoundButtons = () => {
        const isAudible = soundEnabled && activeVideo && !activeVideo.muted;
        soundButtons.forEach((button) => {
          button.hidden = Boolean(isAudible);
        });
      };

      const videoPosterEvents = videos.map((video) => {
        const media = video.closest('.cts-ugc__media');
        const showPoster = () => media?.classList.remove('is-playing');
        const showVideo = () => {
          if (video !== activeVideo || (!sectionIsVisible && !isVideoVisibleInViewport(video)) || document.hidden) {
            video.pause();
            return;
          }

          media?.classList.add('is-playing');
          syncSoundButtons();
        };
        const replayVideo = () => {
          if (video !== activeVideo || !sectionIsVisible || document.hidden) return;
          video.currentTime = 0;
          playVideo(video);
        };

        video.preload = 'none';
        video.addEventListener('playing', showVideo);
        video.addEventListener('pause', showPoster);
        video.addEventListener('error', showPoster);
        video.addEventListener('ended', replayVideo);

        return { video, showPoster, showVideo, replayVideo };
      });

      syncSoundButtons();

      const pauseVideo = (video) => {
        if (!video.paused) video.pause();
      };

      const warmVideo = (video, preload) => {
        if (!video || video.dataset.ctsUgcWarmed === 'auto' || video.dataset.ctsUgcWarmed === preload) return;

        const firstWarm = !video.dataset.ctsUgcWarmed;
        video.dataset.ctsUgcWarmed = preload;
        video.removeAttribute('loading');
        video.preload = preload;
        if (firstWarm || video.networkState === video.NETWORK_EMPTY) video.load();
      };

      const warmVideoAndNext = (video) => {
        const index = videos.indexOf(video);
        if (index < 0) return;

        warmVideo(video, 'auto');
        warmVideo(videos[index + 1], 'metadata');
      };

      const playVideo = (video) => {
        if (!video || document.hidden || !sectionIsVisible || (!autoPlayOnReveal && !hasSwipeInteraction)) return;
        if (activeVideo === video && !video.paused && video.muted === !soundEnabled) return;

        videos.forEach((otherVideo) => {
          if (otherVideo !== video) {
            otherVideo.autoplay = false;
            pauseVideo(otherVideo);
          }
        });

        activeVideo = video;
        warmVideoAndNext(video);
        video.muted = !soundEnabled;
        video.defaultMuted = !soundEnabled;
        video.volume = soundEnabled ? 1 : 0;
        video.autoplay = !soundEnabled;
        syncSoundButtons();

        const attempt = ++playbackAttempt;
        const playback = video.play();
        if (playback) {
          playback.catch((error) => {
            if (activeVideo !== video || attempt !== playbackAttempt) return;

            if (soundEnabled && error?.name === 'NotAllowedError') {
              soundEnabled = false;
              video.muted = true;
              video.defaultMuted = true;
              video.volume = 0;
              syncSoundButtons();
              video.autoplay = true;
              const mutedPlayback = video.play();
              if (mutedPlayback) mutedPlayback.catch(() => {
                enableManualVideoPlayback(video);
                pauseVideo(video);
              });
            } else {
              enableManualVideoPlayback(video);
              pauseVideo(video);
            }
          });
        }
      };

      const getMostVisibleVideo = () => {
        const viewportTop = 0;
        const viewportBottom = window.innerHeight;
        const railRect = rail?.getBoundingClientRect();

        return videos
          .map((video) => {
            const rect = video.getBoundingClientRect();
            const visibleHeight = Math.max(
              0,
              Math.min(rect.bottom, viewportBottom, railRect?.bottom ?? viewportBottom) -
                Math.max(rect.top, viewportTop, railRect?.top ?? viewportTop)
            );
            const visibleWidth = Math.max(
              0,
              Math.min(rect.right, window.innerWidth, railRect?.right ?? window.innerWidth) -
                Math.max(rect.left, 0, railRect?.left ?? 0)
            );
            const availableHeight = Math.min(rect.height, viewportBottom, railRect?.height ?? viewportBottom);
            const availableWidth = Math.min(rect.width, window.innerWidth, railRect?.width ?? window.innerWidth);
            const availableArea = Math.max(0, availableHeight * availableWidth);
            return { video, visibleRatio: availableArea ? (visibleHeight * visibleWidth) / availableArea : 0 };
          })
          .sort((first, second) => second.visibleRatio - first.visibleRatio)[0] || null;
      };

      const playMostVisibleVideo = () => {
        const mostVisible = getMostVisibleVideo();

        if (mostVisible && mostVisible.visibleRatio >= 0.35) {
          playVideo(mostVisible.video);
          return;
        }

        if (activeVideo && manualPlaybackVideos.has(activeVideo) && isVideoVisibleInViewport(activeVideo)) {
          sectionIsVisible = true;
          return;
        }

        activeVideo = null;
        syncSoundButtons();
        videos.forEach(pauseVideo);
      };

      const observer = supportsIntersectionObserver ? new IntersectionObserver(
        (entries) => {
          if (!reducedMotionQuery.matches) {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.1) warmVideoAndNext(entry.target);
            });
          }
          playMostVisibleVideo();
        },
        { threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.7] }
      ) : null;

      const preloadObserver = supportsIntersectionObserver ? new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || reducedMotionQuery.matches) return;
          warmVideoAndNext(videos[0]);
          preloadObserver.disconnect();
        },
        { rootMargin: '600px 0px' }
      ) : null;

      const sectionObserver = supportsIntersectionObserver ? new IntersectionObserver(
        ([entry]) => {
          const manuallyStartedVideoIsVisible = videos.some((video) => manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video));
          sectionIsVisible = entry.isIntersecting || manuallyStartedVideoIsVisible;

          if (!sectionIsVisible) {
            videos.forEach((video) => manualPlaybackVideos.delete(video));
            activeVideo = null;
            syncSoundButtons();
            videos.forEach(pauseVideo);
          } else if (autoPlayOnReveal || hasSwipeInteraction) {
            playMostVisibleVideo();
          }
        },
        { threshold: [0] }
      ) : null;

      if (supportsIntersectionObserver) {
        videos.forEach((video) => observer.observe(video));
        preloadObserver.observe(section);
        sectionObserver.observe(section);
      }

      const startVisibleVideoAfterSwipe = () => {
        hasSwipeInteraction = true;
        playMostVisibleVideo();
      };

      const activateMediaSound = (media) => {
        const video = media?.querySelector('.cts-ugc__video');
        if (!video) return;

        hasSwipeInteraction = true;
        sectionIsVisible = isVideoVisibleInViewport(video);
        soundEnabled = true;
        activeVideo = video;
        videos.forEach((otherVideo) => {
          if (otherVideo !== video) {
            otherVideo.autoplay = false;
            pauseVideo(otherVideo);
          }
        });
        syncSoundButtons();
        playVideoFromTap(video, true);
        warmVideo(videos[videos.indexOf(video) + 1], 'metadata');
      };

      videos.forEach((video) => {
        createVideoPlayButton(video, () => activateMediaSound(video.closest('.cts-ugc__media')));
      });

      let fallbackPlaybackFrame = 0;
      const updateFallbackPlayback = () => {
        fallbackPlaybackFrame = 0;
        sectionIsVisible = videos.some(isVideoVisibleInViewport);
        if (sectionIsVisible && (autoPlayOnReveal || hasSwipeInteraction)) {
          playMostVisibleVideo();
        } else if (!sectionIsVisible) {
          videos.forEach((video) => manualPlaybackVideos.delete(video));
          activeVideo = null;
          syncSoundButtons();
          videos.forEach(pauseVideo);
        }
      };
      const scheduleFallbackPlayback = () => {
        if (supportsIntersectionObserver || fallbackPlaybackFrame) return;
        fallbackPlaybackFrame = window.requestAnimationFrame(updateFallbackPlayback);
      };
      if (!supportsIntersectionObserver) {
        window.addEventListener('scroll', scheduleFallbackPlayback, { passive: true });
        window.addEventListener('resize', scheduleFallbackPlayback);
        scheduleFallbackPlayback();
      }

      const handleMediaClick = (event) => {
        if (event.target instanceof Element && event.target.closest('.cts-ugc__video[controls]')) return;
        const media = event.target instanceof Element && event.target.closest('.cts-ugc__media');
        if (media && section.contains(media)) activateMediaSound(media);
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          videos.forEach((video) => manualPlaybackVideos.delete(video));
          videos.forEach(pauseVideo);
        } else if (hasSwipeInteraction) {
          playMostVisibleVideo();
        } else if (autoPlayOnReveal && sectionIsVisible) {
          playMostVisibleVideo();
        }
        scheduleFallbackPlayback();
      };

      rail?.addEventListener(swipeEndEvent, startVisibleVideoAfterSwipe, { passive: true });
      section.addEventListener('click', handleMediaClick);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      customerMediaControllers.set(section, {
        destroy() {
          observer?.disconnect();
          preloadObserver?.disconnect();
          sectionObserver?.disconnect();
          if (fallbackPlaybackFrame) window.cancelAnimationFrame(fallbackPlaybackFrame);
          window.removeEventListener('scroll', scheduleFallbackPlayback);
          window.removeEventListener('resize', scheduleFallbackPlayback);
          videos.forEach(pauseVideo);
          videoPosterEvents.forEach(({ showPoster }) => showPoster());
          rail?.removeEventListener(swipeEndEvent, startVisibleVideoAfterSwipe);
          section.removeEventListener('click', handleMediaClick);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          videoPosterEvents.forEach(({ video, showPoster, showVideo, replayVideo }) => {
            video.removeEventListener('playing', showVideo);
            video.removeEventListener('pause', showPoster);
            video.removeEventListener('error', showPoster);
            video.removeEventListener('ended', replayVideo);
          });
          customerMediaControllers.delete(section);
        },
      });
    });
  };

  const reviewPlaceholderControllers = new WeakMap();

  const initReviewPlaceholderPlayback = (scope = document) => {
    const sections = [];
    if (scope instanceof Element && scope.matches('.cts-reviews')) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll('.cts-reviews'));

    sections.forEach((section) => {
      if (reviewPlaceholderControllers.has(section)) return;

      const videos = [...section.querySelectorAll('.cts-review-placeholder__video')];
      if (!videos.length) return;

      videos.forEach((video) => {
        video.controls = false;
        video.preload = 'none';
        createVideoPlayButton(video, () => playVideoFromTap(video));
      });

      const playVideo = (video) => {
        if (document.hidden) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.autoplay = true;
        const playback = video.play();
        if (playback) playback.catch(() => {
          enableManualVideoPlayback(video);
          video.pause();
        });
      };

      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.35) playVideo(video);
            else {
              if (manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video) && !document.hidden) return;
              manualPlaybackVideos.delete(video);
              video.autoplay = false;
              if (!video.paused) video.pause();
            }
          });
        },
        { threshold: [0, 0.35] }
      ) : null;

      let fallbackPlaybackFrame = 0;
      const updateFallbackPlayback = () => {
        fallbackPlaybackFrame = 0;
        videos.forEach((video) => {
          const ratio = getViewportIntersectionRatio(video);
          if (ratio >= 0.35) playVideo(video);
          else if (!(manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video))) video.pause();
        });
      };
      const scheduleFallbackPlayback = () => {
        if (observer || fallbackPlaybackFrame) return;
        fallbackPlaybackFrame = window.requestAnimationFrame(updateFallbackPlayback);
      };
      if (observer) videos.forEach((video) => observer.observe(video));
      else {
        window.addEventListener('scroll', scheduleFallbackPlayback, { passive: true });
        window.addEventListener('resize', scheduleFallbackPlayback);
        scheduleFallbackPlayback();
      }

      const handleVisibilityChange = () => {
        if (document.hidden) videos.forEach((video) => {
          manualPlaybackVideos.delete(video);
          video.pause();
        });
        else scheduleFallbackPlayback();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      reviewPlaceholderControllers.set(section, {
        destroy() {
          observer?.disconnect();
          if (fallbackPlaybackFrame) window.cancelAnimationFrame(fallbackPlaybackFrame);
          window.removeEventListener('scroll', scheduleFallbackPlayback);
          window.removeEventListener('resize', scheduleFallbackPlayback);
          videos.forEach((video) => video.pause());
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          reviewPlaceholderControllers.delete(section);
        },
      });
    });
  };

  const getCampaignStorySections = (scope = document) => {
    const sections = [];

    if (scope instanceof Element && scope.matches('.cts-image-text')) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll('.cts-image-text'));

    return sections;
  };

  const initCampaignStoryPlayback = (scope = document) => {
    getCampaignStorySections(scope).forEach((section) => {
      if (storyVideoControllers.has(section)) return;

      const video = section.querySelector('[data-cts-scroll-autoplay] video');
      if (!video) return;

      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      createVideoPlayButton(video, () => playVideoFromTap(video));

      const playVideo = () => {
        if (document.hidden) return;
        video.autoplay = true;
        const playback = video.play();
        if (playback) playback.catch(() => {
          enableManualVideoPlayback(video);
          video.pause();
        });
      };

      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) playVideo();
          else {
            if (manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video) && !document.hidden) return;
            manualPlaybackVideos.delete(video);
            video.autoplay = false;
            if (!video.paused) video.pause();
          }
        },
        { threshold: [0, 0.35] }
      ) : null;

      let fallbackPlaybackFrame = 0;
      const updateFallbackPlayback = () => {
        fallbackPlaybackFrame = 0;
        if (getViewportIntersectionRatio(video) >= 0.35) playVideo();
        else if (!(manualPlaybackVideos.has(video) && isVideoVisibleInViewport(video))) {
          video.autoplay = false;
          video.pause();
        }
      };
      const scheduleFallbackPlayback = () => {
        if (observer || fallbackPlaybackFrame) return;
        fallbackPlaybackFrame = window.requestAnimationFrame(updateFallbackPlayback);
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          manualPlaybackVideos.delete(video);
          video.pause();
        } else scheduleFallbackPlayback();
      };

      if (observer) observer.observe(video);
      else {
        window.addEventListener('scroll', scheduleFallbackPlayback, { passive: true });
        window.addEventListener('resize', scheduleFallbackPlayback);
        scheduleFallbackPlayback();
      }
      document.addEventListener('visibilitychange', handleVisibilityChange);

      storyVideoControllers.set(section, {
        destroy() {
          observer?.disconnect();
          if (fallbackPlaybackFrame) window.cancelAnimationFrame(fallbackPlaybackFrame);
          window.removeEventListener('scroll', scheduleFallbackPlayback);
          window.removeEventListener('resize', scheduleFallbackPlayback);
          video.pause();
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          storyVideoControllers.delete(section);
        },
      });
    });
  };

  const init = (scope = document) => {
    syncLooxProduct();
    initLooxPlaceholderCards(scope);
    initReviewDialogButtons(scope);
    initSwipeRails(scope);
    initCustomerMediaPlayback(scope);
    initReviewPlaceholderPlayback(scope);
    initCampaignStoryPlayback(scope);
    initMobileGradientParallax();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    looxReviewCardControllers.get(event.target)?.destroy();
    const reviewSections = [];
    if (event.target instanceof Element && event.target.matches('.cts-reviews')) reviewSections.push(event.target);
    if (event.target.querySelectorAll) reviewSections.push(...event.target.querySelectorAll('.cts-reviews'));
    reviewSections.forEach((section) => {
      section.querySelectorAll('loox-dynamic-carousel-widget[data-cts-placeholder-card-layout]').forEach((widget) => {
        looxReviewCardControllers.get(widget)?.destroy();
      });
      reviewDialogControllers.get(section)?.destroy();
      const modal = getReviewDialog(section);
      modal?.remove();
    });
    getSwipeRails(event.target).forEach((rail) => swipeRailControllers.get(rail)?.destroy());
    customerMediaControllers.get(event.target)?.destroy();
    reviewPlaceholderControllers.get(event.target)?.destroy();
    storyVideoControllers.get(event.target)?.destroy();
  });
})();
